import type { ApiResponse, ContactNoteDto, ListContactNotesResponse } from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';
import api from '@/libs/axiosInstance';

/** Shared key so the read hook and the create mutation target the same cache entry. */
export const contactNotesQueryKey = (creatorIdentityId: string | null) =>
  ['contact-notes', creatorIdentityId] as const;

const fetchContactNotes = async (creatorIdentityId: string): Promise<ContactNoteDto[]> => {
  const { data } = await api.get<ApiResponse<ListContactNotesResponse>>(
    `/dashboard/contacts/${creatorIdentityId}/notes`,
    { params: { page: 1, per_page: 50 } }
  );

  return data.data?.data ?? [];
};

/**
 * Lazy notes slice for the unified contact card, keyed by `creatorIdentityId`.
 * Only fires when the NOTES tab mounts. Notes are a flat, brand-scoped list
 * (newest first), so this is a plain `useQuery`, not an infinite one. Returns the
 * already-flattened `ContactNoteDto[]`.
 */
export const useContactNotesQuery = (creatorIdentityId: string | null) => {
  return useQuery({
    queryKey: contactNotesQueryKey(creatorIdentityId),
    queryFn: () => fetchContactNotes(creatorIdentityId!),
    enabled: !!creatorIdentityId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
